/**
 * Execution-time nodes for a run: resolves the workflow snapshot the run was
 * pinned to (draft or published) and maps it into canvas `WorkflowNode` shape.
 * Falls back to the live definition nodes for legacy runs without a snapshot
 * ref. Canonical revision runs stay empty until their pinned snapshot loads so
 * a changed live definition cannot be presented as execution-time truth.
 *
 * `fromSnapshot` tells consumers whether the returned nodes are the
 * execution-time truth; per-step introspection only trusts node config over
 * the persisted `nodeIntrospection` string when it is.
 */
import React from 'react';
import type { WorkflowNode } from '../../../../types/workflow';
import {
  useDraftSnapshot,
  useWorkflowRevision,
  useWorkflowSnapshot,
} from '../hooks/useWorkflowSnapshots';
import { snapshotToWorkflowNodes } from '../hooks/snapshotNodes';

interface RunSnapshotRef {
  snapshotVersion?: number | null;
  draftSnapshot?: { version: string } | null;
  workflowRevision?: { number: number } | null;
}

export interface RunSnapshotNodes {
  nodes: WorkflowNode[];
  fromSnapshot: boolean;
}

export function useRunSnapshotNodes(
  workflowId: string | null,
  run: RunSnapshotRef | null | undefined,
  liveNodes: WorkflowNode[]
): RunSnapshotNodes {
  const revisionNumber = run?.workflowRevision?.number ?? null;
  const draftVersion =
    revisionNumber === null ? (run?.draftSnapshot?.version ?? null) : null;
  const publishedVersion =
    revisionNumber === null &&
    draftVersion === null &&
    typeof run?.snapshotVersion === 'number' &&
    run.snapshotVersion > 0
      ? run.snapshotVersion
      : null;

  const revisionQuery = useWorkflowRevision(workflowId, revisionNumber);
  const draftQuery = useDraftSnapshot(workflowId, draftVersion);
  const publishedQuery = useWorkflowSnapshot(workflowId, publishedVersion);

  return React.useMemo(() => {
    const snapshotJson =
      revisionNumber !== null
        ? revisionQuery.data?.snapshotJson
        : draftVersion !== null
          ? draftQuery.data?.snapshotJson
          : publishedQuery.data?.snapshotJson;
    if (snapshotJson && Array.isArray(snapshotJson.nodes) && snapshotJson.nodes.length > 0) {
      const mapped = snapshotToWorkflowNodes(snapshotJson.nodes);
      if (mapped.length > 0) return { nodes: mapped, fromSnapshot: true };
    }
    return {
      nodes: revisionNumber === null ? liveNodes : [],
      fromSnapshot: false,
    };
  }, [
    revisionNumber,
    revisionQuery.data,
    draftVersion,
    draftQuery.data,
    publishedQuery.data,
    liveNodes,
  ]);
}
